import React from "react";
import TaskItem from "./TaskItem.js";
import { Link } from "react-router-dom";
import { useTeamSchedule } from "./TeamScheduleContext.js";
import { DateTime } from "luxon";
import { cn, groupBy, useIsGummy } from "@swivu/shared-ui";
import { TeamScheduleRow } from "./groupTeamSchedule.js";

type TeamSchedule = ReturnType<typeof useTeamSchedule>["gridData"];
type DateRange = ReturnType<typeof useTeamSchedule>["dateRange"];

type RowGridProps = {
  teamSchedule: TeamSchedule | undefined;
  dateRange: DateRange;
  timeZone: string;
};

const RowGrid = ({ teamSchedule, dateRange, timeZone }: RowGridProps) => {
  const isGummy = useIsGummy();
  return (
    <>
      {teamSchedule &&
        teamSchedule
          ?.sort((a, b) => a.rowLabel.localeCompare(b.rowLabel))
          .map((resource, _resourceIndex) => {
            return (
              <div
                key={resource.resourceId}
                className=" relative grid-cols-subgrid grid col-span-5 @3xl:col-span-6 gap-2 pt-2 [&>a]:py-3 "
              >
                {isGummy ? (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute top-1 -left-2 h-[calc(100%-0.5rem)] w-[3px] rounded-full bg-gradient-to-b from-mono-300 to-mono-400 dark:from-mono-600 dark:to-mono-700"
                  />
                ) : (
                  <div
                    className="absolute top-0 -left-2 h-full bg-mono-400 dark:bg-mono-600"
                    style={{ width: "2px" }}
                  />
                )}
                <RowGridItem
                  key={resource.rowLabel + _resourceIndex}
                  resource={resource}
                  index={_resourceIndex}
                  dateRange={dateRange}
                  timeZone={timeZone}
                />
              </div>
            );
          })}
    </>
  );
};

const gridColumns = (
  date: TeamScheduleRow["dates"][number],
  dateRange: DateRange,
  timeZone: string
) => {
  const startDateTime = DateTime.fromISO(date.start_time, {
    zone: timeZone
  });
  const firstDate = DateTime.fromJSDate(dateRange[0], {
    zone: timeZone
  }).startOf("day");

  const daysDiffStart = Math.min(
    dateRange.length - 1,
    Math.max(
      0,
      startDateTime.startOf("day").diff(firstDate.startOf("day"), "days").days
    )
  );

  const endDateTime = DateTime.fromISO(date.end_time);

  const daysDiffEnd = Math.min(
    dateRange.length,
    Math.ceil(endDateTime.diff(firstDate, "days").days)
  );
  return { daysDiffStart, daysDiffEnd };
};

const RowGridItem = ({
  resource,
  dateRange,
  index,
  timeZone
}: {
  resource: NonNullable<TeamSchedule>[number];
  dateRange: ReturnType<typeof useTeamSchedule>["dateRange"];
  index: number;
  timeZone: string;
}) => {
  const isGummy = useIsGummy();
  return (
    <React.Fragment key={`${resource.rowLabel}${index}`}>
      {resource.dates.length > 0 &&
        (isGummy ? (
          <div
            aria-hidden
            className="pointer-events-none absolute mt-1 top-0 -left-2 h-[calc(100%-0.5rem)] w-[3px] rounded-full bg-gradient-to-b from-mono-300 to-mono-400 dark:from-mono-600 dark:to-mono-700"
          />
        ) : (
          <div
            className="absolute mt-0.5 top-0 -left-2 h-full bg-mono-400 dark:bg-mono-600"
            style={{ width: "2px" }}
          />
        ))}
      <div
        className={`col-span-5 @3xl:col-span-6  flex items-center @3x:items-start  ml-0.5 `}
      >
        <Link
          preventScrollReset={true}
          to={`${resource.rowLink}`}
          className={cn(
            "mobile-pref-list-button m-0 px-2 py-3",
            "rounded-surface-md",
            isGummy &&
              "transition-transform duration-150 gummy-lift active:translate-y-0 active:scale-[0.97] motion-reduce:transform-none hover:bg-mono-200/60 dark:hover:bg-mono-800/60"
          )}
        >
          <p
            className={cn(
              "mobile-pref-card-title leading-tight",
              isGummy
                ? "text-lg font-display font-black tracking-tight text-mono-900 dark:text-white leading-5"
                : "font-bold"
            )}
          >
            {resource.rowLabel}
          </p>
        </Link>
      </div>

      <RowRenderer
        resource={resource}
        dateRange={dateRange}
        timeZone={timeZone}
      />
    </React.Fragment>
  );
};

const RowRenderer = ({
  resource,
  dateRange,
  timeZone
}: {
  resource: TeamScheduleRow;
  dateRange: DateRange;
  timeZone: string;
}) => {
  const rows = groupBy(resource?.dates ?? [], "row");
  // console.log("rows", rows);
  // return <pre>{JSON.stringify(rows, null, 2)}</pre>;
  return (
    <>
      {Object.entries(rows).map(
        ([row, dates]: [string, Array<(typeof resource.dates)[0]>]) => {
          return (
            <div
              key={row}
              className="col-span-5 @3xl:col-span-5 @3xl:grid-cols-5 grid grid-cols-5 gap-1"
            >
              {(dates as Array<(typeof resource.dates)[0]>)
                // compare start dates to sort
                .sort((a, b) => {
                  const aStart = DateTime.fromISO(a.start_time, {
                    zone: timeZone
                  });
                  const bStart = DateTime.fromISO(b.start_time, {
                    zone: timeZone
                  });
                  return aStart.diff(bStart, "days").days;
                })
                .map((date) => {
                  const { daysDiffStart, daysDiffEnd } = gridColumns(
                    date,
                    dateRange,
                    timeZone
                  );
                  const startTime = DateTime.fromISO(date.start_time, {
                    zone: timeZone
                  });
                  const endTime = DateTime.fromISO(date.end_time, {
                    zone: timeZone
                  });
                  // is startTime within dateRange?
                  const isStartTimeWithinDateRange = dateRange.find((date) =>
                    startTime.hasSame(
                      DateTime.fromJSDate(date, { zone: timeZone }),
                      "day"
                    )
                  );
                  const isEndTimeWithinDateRange = dateRange.find((date) =>
                    endTime.hasSame(
                      DateTime.fromJSDate(date, { zone: timeZone }),
                      "day"
                    )
                  );

                  const startsAfterMidnight =
                    isStartTimeWithinDateRange && startTime.hour !== 0;
                  const endsBeforeMidnight =
                    isEndTimeWithinDateRange && endTime.hour !== 0;
                  const plot = `${daysDiffStart + 1} / ${daysDiffEnd + 1}`;
                  return (
                    <div
                      className=""
                      key={date.resourceId + date.start_time}
                      style={{
                        gridColumn: plot
                      }}
                    >
                      <TaskItem
                        key={resource.resourceId + plot}
                        taskId={date.resourceId}
                        color={date.color}
                        taskDisplayName={date.label}
                        // taskDisplayName={`${plot} - ${date.row}`}
                        // taskDisplayName={`${date.resourceId}`}
                        link={date.dateLink}
                        style={date.style}
                        // viewTransitions={dates?.[i].viewTransitions}
                        // viewTransitionUnique={`${i}`}
                        startsAt={
                          startsAfterMidnight
                            ? startTime.toFormat("h:mm a").toLowerCase()
                            : undefined
                        }
                        endsAt={
                          endsBeforeMidnight
                            ? endTime.toFormat("h:mm a").toLowerCase()
                            : undefined
                        }
                      />
                    </div>
                  );
                })}
            </div>
          );
        }
      )}
    </>
  );
};

export default RowGrid;
